import { useCallback, useEffect, useState } from "react";
import { Plus, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_CLS = {
  open: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#B7791F]",
  in_progress: "border-[#0A2540]/30 bg-[#0A2540]/10 text-[#0A2540]",
  resolved: "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]",
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await API.get("/support/tickets");
      setTickets(r.data);
      setThread((t) => (t ? r.data.find((x) => x.id === t.id) || null : null));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (subject.trim().length < 3 || message.trim().length < 3) return toast.error("Enter a subject and message");
    setBusy(true);
    try {
      await API.post("/support/tickets", { subject, message });
      toast.success("Ticket raised — our team will respond soon");
      setOpen(false);
      setSubject("");
      setMessage("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const r = await API.post(`/support/tickets/${thread.id}/reply`, { message: reply });
      setThread(r.data);
      setReply("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">Raise an issue and track its resolution</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ticket-button"><Plus size={16} className="mr-1" weight="bold" /> New Ticket</Button>
          </DialogTrigger>
          <DialogContent data-testid="ticket-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Raise a Support Ticket</DialogTitle>
              <DialogDescription className="sr-only">Create a support ticket</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. GST report mismatch" data-testid="ticket-subject-input" />
              </div>
              <div className="space-y-2">
                <Label>Describe the issue</Label>
                <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What happened, and what did you expect?" data-testid="ticket-message-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="ticket-cancel-button">Cancel</Button>
                <Button onClick={create} disabled={busy} data-testid="ticket-submit-button">{busy ? "Submitting…" : "Submit Ticket"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {tickets.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center" data-testid="empty-state">
            <ChatCircle size={32} className="mx-auto text-muted-foreground" />
            <p className="mt-2 font-heading text-lg font-semibold">No tickets yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Facing an issue? Raise a ticket and track it here.</p>
          </div>
        ) : (
          tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setThread(t)}
              className="flex w-full items-center justify-between rounded-lg border bg-card p-4 text-left transition-transform duration-150 hover:-translate-y-0.5 hover:shadow-sm"
              data-testid={`ticket-row-${t.id}`}
            >
              <div>
                <p className="font-medium">{t.subject}</p>
                <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")} · {t.messages.length} message{t.messages.length !== 1 ? "s" : ""}</p>
              </div>
              <Badge variant="outline" className={`capitalize ${STATUS_CLS[t.status] || ""}`} data-testid={`ticket-status-${t.id}`}>{t.status.replace("_", " ")}</Badge>
            </button>
          ))
        )}
      </div>

      <Dialog open={!!thread} onOpenChange={(v) => !v && setThread(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" data-testid="ticket-thread-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{thread?.subject}</DialogTitle>
            <DialogDescription className="sr-only">Ticket conversation thread</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {thread?.messages.map((m, i) => (
              <div key={i} className={`rounded-md border p-3 text-sm ${m.from === "admin" ? "ml-8 border-primary/30 bg-primary/5" : "mr-8 bg-muted/40"}`}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {m.from === "admin" ? "Support Team" : "You"} · {new Date(m.at).toLocaleString("en-IN")}
                </p>
                <p className="whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="ticket-reply-input" />
            <Button onClick={sendReply} disabled={busy} data-testid="ticket-reply-button">Send</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
